import React, { useState } from 'react';
import { Shield, AlertTriangle, Calculator, FileText, Camera, Users, Zap } from 'lucide-react';

const ConstructionMoEDemo = () => {
  const [inputText, setInputText] = useState('');
  const [activeExperts, setActiveExperts] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(0);

  const experts = [
    { 
      id: 'safety', 
      name: 'Safety Risk', 
      icon: <Shield className="w-6 h-6" />, 
      color: 'bg-red-500', 
      expertise: ['safety', 'injury', 'accident', 'ppe', 'osha', 'fall', 'hazard'] 
    },
    { 
      id: 'property', 
      name: 'Property Damage', 
      icon: <AlertTriangle className="w-6 h-6" />, 
      color: 'bg-orange-500', 
      expertise: ['damage', 'fire', 'water', 'theft', 'vandalism', 'weather', 'storm'] 
    },
    { 
      id: 'liability', 
      name: 'Liability Claims', 
      icon: <Users className="w-6 h-6" />, 
      color: 'bg-purple-500', 
      expertise: ['liability', 'bodily', 'third party', 'public', 'injury', 'lawsuit'] 
    },
    { 
      id: 'equipment', 
      name: 'Equipment Coverage', 
      icon: <Camera className="w-6 h-6" />, 
      color: 'bg-blue-500', 
      expertise: ['equipment', 'crane', 'excavator', 'machinery', 'breakdown', 'repair'] 
    },
    { 
      id: 'workers', 
      name: 'Workers Comp', 
      icon: <Users className="w-6 h-6" />, 
      color: 'bg-green-500', 
      expertise: ['workers', 'compensation', 'employee', 'workplace', 'occupational', 'injury'] 
    },
    { 
      id: 'financial', 
      name: 'Financial Analysis', 
      icon: <Calculator className="w-6 h-6" />, 
      color: 'bg-indigo-500', 
      expertise: ['premium', 'deductible', 'cost', 'calculate', 'rate', 'pricing'] 
    },
    { 
      id: 'regulatory', 
      name: 'Compliance', 
      icon: <FileText className="w-6 h-6" />, 
      color: 'bg-teal-500', 
      expertise: ['compliance', 'regulation', 'permit', 'code', 'inspection', 'violation'] 
    }
  ];

  const sampleInputs = [
    "Worker fell from 20-foot scaffolding, what's our liability exposure?",
    "Equipment crane tipped over damaging neighboring building",
    "Calculate premium for $50M high-rise project with 200 workers",
    "Site flooded during storm, materials worth $2M damaged",
    "OSHA cited us for safety violations, impact on coverage?",
    "Subcontractor injury claim - who's responsible?",
    "Fire destroyed half-completed office building"
  ];

  const determineActiveExperts = (text) => {
    const words = text.toLowerCase().split(/\s+|[.,!?]/);
    return experts.filter(expert => 
      expert.expertise.some(keyword => 
        words.some(word => word.includes(keyword) || keyword.includes(word))
      )
    ).slice(0, 4); // Maximum 4 active experts for construction scenarios
  };

  const processInput = async () => {
    if (!inputText.trim()) return;
    
    setIsProcessing(true);
    setStep(1);

    // Step 1: Router analyzes input
    await new Promise(resolve => setTimeout(resolve, 1500));
    const relevant = determineActiveExperts(inputText);
    setActiveExperts(relevant);
    setStep(2);

    // Step 2: Experts process
    await new Promise(resolve => setTimeout(resolve, 2000));
    setStep(3);

    // Step 3: Merge results
    await new Promise(resolve => setTimeout(resolve, 1500));
    setStep(4);
    setIsProcessing(false);
  };

  const resetDemo = () => {
    setInputText('');
    setActiveExperts([]);
    setStep(0);
    setIsProcessing(false);
  };

  const getExpertOutput = (expert) => {
    const outputs = {
      safety: "Risk Assessment: High-severity incident, potential OSHA fine $15K, recommend safety training review",
      property: "Coverage Analysis: Builder's risk policy covers storm damage up to $5M, $25K deductible applies",
      liability: "Liability Exposure: General liability covers third-party claims up to $2M per occurrence",
      equipment: "Equipment Claim: Covered under equipment floater, estimated repair cost $125K, rental coverage included",
      workers: "Workers Comp: Claim covered, medical benefits + wage replacement, rate impact assessment needed",
      financial: "Premium Impact: Calculated 15% increase based on claim history, new rate $45K annually",
      regulatory: "Compliance Check: Violation affects coverage, required corrective action plan within 30 days"
    };
    return outputs[expert.id] || "Processing specialized insurance analysis...";
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gradient-to-br from-orange-50 to-red-100 min-h-screen">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">Construction Insurance MoE System</h1>
        <p className="text-center text-gray-600 mb-8">See how AI activates specialized insurance experts for different claim types</p>

        {/* Input Section */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h2 className="text-lg font-semibold mb-4">Submit Insurance Scenario</h2>
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Describe a construction incident, claim, or risk scenario..."
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
              onKeyPress={(e) => e.key === 'Enter' && processInput()}
            />
            <button
              onClick={processInput}
              disabled={!inputText.trim() || isProcessing}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-400 transition-colors"
            >
              Analyze
            </button>
          </div>

          <div className="space-y-2">
            <span className="text-sm text-gray-600">Try these construction scenarios:</span>
            <div className="grid grid-cols-1 gap-2">
              {sampleInputs.map((sample, index) => (
                <button
                  key={index}
                  onClick={() => setInputText(sample)}
                  className="text-sm bg-orange-100 text-orange-700 px-3 py-2 rounded-lg hover:bg-orange-200 transition-colors text-left"
                >
                  {sample}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Expert Pool */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-6 text-center">Insurance Specialist Experts</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {experts.map((expert) => {
              const isActive = activeExperts.find(ae => ae.id === expert.id);
              return (
                <div
                  key={expert.id}
                  className={`p-4 rounded-lg border-2 text-center transition-all duration-500 ${
                    isActive && step >= 2
                      ? `${expert.color} border-white text-white shadow-lg scale-110 animate-pulse`
                      : 'bg-gray-100 border-gray-300 text-gray-600'
                  }`}
                >
                  <div className="flex justify-center mb-2">
                    {expert.icon}
                  </div>
                  <div className="text-sm font-medium">{expert.name}</div>
                  {isActive && step >= 2 && (
                    <div className="mt-2">
                      <div className="w-2 h-2 bg-white rounded-full mx-auto animate-pulse"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Processing Steps */}
        {step > 0 && (
          <div className="space-y-6">
            {/* Router Analysis */}
            <div className={`border-2 rounded-lg p-6 ${
              step >= 1 ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <Zap className="w-6 h-6 text-yellow-600" />
                <h3 className="text-lg font-semibold">Step 1: Insurance Router Analysis</h3>
                {step === 1 && <div className="animate-spin w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full"></div>}
              </div>
              <p className="text-gray-700">
                Analyzing scenario: "<em>{inputText}</em>"<br />
                Determining which insurance specialists need to evaluate this case...
              </p>
            </div>

            {/* Expert Activation */}
            {step >= 2 && (
              <div className="border-2 border-green-400 bg-green-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-6 h-6 text-green-600" />
                  <h3 className="text-lg font-semibold">Step 2: Specialist Activation</h3>
                  {step === 2 && <div className="animate-spin w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full"></div>}
                </div>
                <p className="text-gray-700 mb-4">
                  Activated {activeExperts.length} of {experts.length} specialists 
                  ({((activeExperts.length / experts.length) * 100).toFixed(0)}% of insurance expertise deployed)
                </p>
                
                {step >= 3 && (
                  <div className="space-y-3">
                    {activeExperts.map((expert) => (
                      <div key={expert.id} className="bg-white p-4 rounded border border-green-200">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-2 rounded-full ${expert.color} text-white`}>
                            {expert.icon}
                          </div>
                          <span className="font-medium">{expert.name} Specialist</span>
                        </div>
                        <p className="text-sm text-gray-700">{getExpertOutput(expert)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Merge Process */}
            {step >= 4 && (
              <div className="border-2 border-purple-400 bg-purple-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <Zap className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold">Step 3: Comprehensive Assessment</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  Combining analysis from {activeExperts.length} specialists to create comprehensive insurance response...
                </p>
                
                <div className="bg-white p-6 rounded border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-800 mb-3">📋 Final Insurance Assessment</h4>
                  <div className="space-y-3">
                    {activeExperts.map((expert, index) => (
                      <div key={expert.id} className="flex items-start gap-3">
                        <div className={`p-1 rounded-full ${expert.color} text-white`}>
                          {expert.icon}
                        </div>
                        <div>
                          <span className="font-medium text-gray-800">{expert.name}:</span>
                          <span className="text-gray-700 ml-2">{getExpertOutput(expert)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Efficiency Comparison */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-red-800 mb-3">❌ Traditional Insurance AI</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Active Specialists:</span>
                <span className="font-bold text-red-700">All 7 (100%)</span>
              </div>
              <div className="w-full bg-red-200 rounded-full h-4">
                <div className="bg-red-500 h-4 rounded-full w-full"></div>
              </div>
              <p className="text-sm text-red-700">Every specialist analyzes every claim = slow, expensive processing</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-green-800 mb-3">✅ MoE Insurance AI</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Active Specialists:</span>
                <span className="font-bold text-green-700">
                  {activeExperts.length > 0 ? `${activeExperts.length} of 7` : '0 of 7'} 
                  ({activeExperts.length > 0 ? Math.round((activeExperts.length / experts.length) * 100) : 0}%)
                </span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-4">
                <div 
                  className="bg-green-500 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${activeExperts.length > 0 ? (activeExperts.length / experts.length) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-sm text-green-700">Only relevant specialists activated = fast, cost-effective analysis</p>
            </div>
          </div>
        </div>

        {/* Reset Button */}
        {step > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={resetDemo}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors mr-4"
            >
              Try Another Scenario
            </button>
          </div>
        )}

        {/* Industry Benefits */}
        <div className="mt-8 bg-gradient-to-r from-amber-50 to-yellow-100 border border-amber-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-amber-800 mb-3">🏗️ Construction Insurance MoE Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl mb-2">⚡</div>
              <h4 className="font-semibold text-amber-700">Faster Claims</h4>
              <p className="text-sm text-amber-600">Route to right specialists immediately</p>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-2">💰</div>
              <h4 className="font-semibold text-amber-700">Lower Costs</h4>
              <p className="text-sm text-amber-600">Use only needed expertise per case</p>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-2">🎯</div>
              <h4 className="font-semibold text-amber-700">Better Accuracy</h4>
              <p className="text-sm text-amber-600">Specialists handle their domain expertise</p>
            </div>
          </div>
        </div>

        {/* Real-World Construction Example */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-blue-800 mb-3">🏢 Real Example: Major Insurer Implementation</h3>
          <p className="text-blue-700 mb-4">
            A leading construction insurer implemented MoE architecture across their claims processing system. 
            Instead of running all insurance analysis models for every claim, they route claims to specialized experts:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Results Achieved:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• 60% reduction in processing time</li>
                <li>• 35% improvement in claim accuracy</li>
                <li>• 45% decrease in computational costs</li>
                <li>• 80% faster fraud detection</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-blue-800 mb-2">Specialist Routing:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Fall injuries → Safety + Workers Comp experts</li>
                <li>• Equipment damage → Equipment + Property experts</li>
                <li>• Weather claims → Property + Financial experts</li>
                <li>• Regulatory issues → Compliance + Legal experts</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cost Savings Visualization */}
        <div className="mt-6 bg-gradient-to-r from-green-50 to-emerald-100 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-green-800 mb-4">💵 Processing Cost Comparison</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-700 mb-3">Traditional System</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Cost per claim analysis:</span>
                  <span className="font-bold text-red-600">$45</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Processing time:</span>
                  <span className="font-bold text-red-600">3.5 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Annual volume (10K claims):</span>
                  <span className="font-bold text-red-600">$450K</span>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-700 mb-3">MoE System</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm">Cost per claim analysis:</span>
                  <span className="font-bold text-green-600">$18</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Processing time:</span>
                  <span className="font-bold text-green-600">1.2 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">Annual volume (10K claims):</span>
                  <span className="font-bold text-green-600">$180K</span>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-4">
            <div className="text-2xl font-bold text-green-700">$270K Annual Savings</div>
            <div className="text-sm text-green-600">60% cost reduction with MoE architecture</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstructionMoEDemo;
